
import { useCallback, useState } from 'react';
import { useTaskData } from './hooks/useTaskData';
import { useTaskRefetch } from './hooks/useTaskRefetch';
import { useTaskCount } from './hooks/useTaskCount';
import { 
  useLocalTaskState, 
  useViewTransition,
  useTaskMutations
} from './hooks';
import { TaskContextType } from './TaskContext';
import { DateRangeOption } from '@/hooks/tasks/use-task-operations';

export const useTaskProvider = (): TaskContextType => {
  // Set up view transition state first
  const defaultDateRange: DateRangeOption = 'week';
  
  // All/Now toggle state - default to false (All mode)
  const [showOnlyCurrentRange, setShowOnlyCurrentRange] = useState(false);
  
  // Initialize view transition hooks BEFORE task operations
  const { 
    dateRange, 
    setDateRange, 
    currentDate, 
    setCurrentDate, 
    isChangingView 
  } = useViewTransition(defaultDateRange);
  
  // Fetch task data
  const {
    user,
    dbActiveTasks,
    dbCompletedTasks,
    isLoading,
    dbAddTask,
    dbUpdateTask,
    dbRemoveTask,
    refetch
  } = useTaskData(dateRange, currentDate, showOnlyCurrentRange);
  
  // Handle task refetching
  const { refetchTasks } = useTaskRefetch(dateRange, currentDate, refetch);
  
  // Manage local task state with optimized logic
  const {
    localActiveTasks,
    localCompletedTasks,
    setLocalActiveTasks,
    setLocalCompletedTasks
  } = useLocalTaskState(dbActiveTasks, dbCompletedTasks, isLoading, isChangingView, false);
  
  // Calculate task counts
  const { activeTaskCount } = useTaskCount(localActiveTasks);
  
  // Set up task mutations
  const {
    addTask,
    updateTaskOrder,
    completeTask,
    uncompleteTask,
    removeTask,
    updateTask,
    clearCompletedTasks
  } = useTaskMutations(
    user,
    false, // No workspace error
    localActiveTasks,
    localCompletedTasks,
    setLocalActiveTasks,
    setLocalCompletedTasks,
    dbAddTask || (() => {}),
    dbUpdateTask || (() => {}),
    dbRemoveTask || (() => {})
  );

  return {
    activeTasks: localActiveTasks,
    completedTasks: localCompletedTasks,
    activeTaskCount,
    isLoading: isLoading && !isChangingView,
    setActiveTasks: setLocalActiveTasks,
    setCompletedTasks: setLocalCompletedTasks,
    addTask,
    completeTask,
    uncompleteTask,
    removeTask,
    clearCompletedTasks,
    updateTask,
    updateTaskOrder,
    setDateRange,
    setCurrentDate,
    dateRange,
    currentDate,
    refetchTasks,
    showOnlyCurrentRange,
    setShowOnlyCurrentRange
  };
};
